import {
  Component,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { UtilsService } from 'src/app/shared/utils.service';
import { AppService } from 'src/app/app.service';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import { RBACService } from '../../shared/rbac.service';
import { TaskAnalysesEditComponent } from 'src/app/task-analyses/task-analyses-edit/task-analyses-edit.component';
import { tap } from 'rxjs/operators';
import { CustomDataSource } from 'src/app/utils/custom-data-source';
import { ActivatedRoute, Router } from '@angular/router';
import { TaskAnalysesViewComponent } from 'src/app/task-analyses/task-analyses-view/task-analyses-view.component';
import { SiteModel } from '../../models/site.model';
import { SitesTaskAnalysesFilterComponent } from '../sites-task-analyses-filter/sites-task-analyses-filter.component';
import { TaskAnalysisModel } from '../../models/task-analysis.model';

@Component({
  selector: 'app-sites-task-analyses',
  templateUrl: './sites-task-analyses.component.html',
  styleUrls: ['./sites-task-analyses.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SitesTaskAnalysesComponent implements OnInit {
  // RBAC permission flags: resolved once in ngOnInit().
  canCreate: boolean = false;
  canView: boolean = false;
  canEdit: boolean = false;
  canDelete: boolean = false;

  // Build displayedColumns dynamically: NOT as a static array.
  displayedColumns: string[] = [];

  dataSource = new TaskAnalysisDataSource(this.app, this.api);

  // the paginator and sorter
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  parent_id: number;
  child_id: number;
  site_id: number;

  constructor(
    public utils: UtilsService,
    public app: AppService,
    private api: ApiRequestService,
    private route: ActivatedRoute,
    public rbac: RBACService,
    private router: Router
  ) {}

  ngOnInit() {
    // 1. Resolve RBAC permission flags.
    this.canCreate = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_TASK_ANALYSIS_DOCUMENTS,
          RBACService.RBAC_A.A_CREATE
        )
      ],
      true
    );
    this.canView = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_TASK_ANALYSIS_DOCUMENTS,
          RBACService.RBAC_A.A_VIEW
        )
      ],
      true
    );
    this.canEdit = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_TASK_ANALYSIS_DOCUMENTS,
          RBACService.RBAC_A.A_EDIT
        )
      ],
      true
    );
    this.canDelete = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_TASK_ANALYSIS_DOCUMENTS,
          RBACService.RBAC_A.A_DELETE
        )
      ],
      true
    );

    // 2. Page-level access: redirect if user has NO permissions at all.
    if (!this.canCreate && !this.canView && !this.canEdit && !this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to access task analysis documents.'
      );
      this.router.navigate(['/sites']);
      return;
    }

    // 3. Build displayedColumns dynamically.
    this.displayedColumns = [
      'id',
      'sites',
      'task_name',
      'date_created',
      'actions'
    ];
    if (this.canDelete) {
      this.displayedColumns.unshift('select');
    }

    // Get the site id from the route params.
    this.parent_id = Number(this.route.parent.snapshot.params['parent_id']);
    this.child_id = Number(this.route.parent.snapshot.params['child_id']);

    // Check if we are updating a site or creating a new one.
    if (
      this.child_id ||
      (typeof this.route.parent.snapshot.params['child_id'] == 'undefined' &&
        this.parent_id)
    ) {
      // Store the site id.
      this.site_id = this.child_id ? this.child_id : this.parent_id;

      this.dataSource.site_ids.push(this.site_id);

      // 4. Only load data if user has view permission.
      if (this.canView) {
        this.dataSource.getData();
      }
    }
  }

  /**
   * Handles the add operation by opening the edit dialog for creating a new task analysis.
   */
  onAdd(): void {
    if (!this.canCreate) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to create task analysis documents.'
      );
      return;
    }
    this.openEditDialog();
  }

  /**
   * Opens a dialog to edit task analyses.
   */
  onEdit(id?: number | string): void {
    if (!this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to edit task analysis documents.'
      );
      return;
    }
    this.openEditDialog(id);
  }

  /**
   * Shared private method to open the edit dialog for both create and edit operations.
   */
  private openEditDialog(id?: number | string): void {
    this.utils.showComponentDialog(
      TaskAnalysesEditComponent,
      {
        task_analysis_id: id,
        site_id: this.site_id,
        feature: RBACService.RBAC_F.F_SITES_TASK_ANALYSIS_DOCUMENTS
      },
      {
        width: '700px'
      },
      (success: boolean): void => {
        if (this.canView) {
          this.dataSource.getData(true);
        }
      }
    );
  }

  onView(id: number) {
    if (!this.canView) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to view task analysis documents.'
      );
      return;
    }
    this.utils.showComponentDialog(
      TaskAnalysesViewComponent,
      id,
      { width: '700px' },
      (success: boolean) => {
        // if (!success) { return; }
        // this.dataSource.getData(true);
      }
    );
  }

  ngAfterViewInit() {
    // The table, its matSort and its mat-paginator all live inside a container gated by
    // *ngIf="canView", so without that permission Angular never creates those view
    // children and this.sort / this.paginator stay undefined. Bail out before wiring them up.
    if (!this.canView) {
      return;
    }

    // reset the paginator when sorting
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.sort_by = this.sort.active;
      this.dataSource.sort_order = this.sort.direction;

      // Sort UTC columns by its corresponding date column.
      if (this.sort.active == 'date_created_UTC') {
        this.dataSource.sort_by = 'date_created';
      }

      this.dataSource.getData();
    });

    // subscribe to the paginator tap events
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  onClearSearch() {
    this.dataSource.search = '';
    this.dataSource.getData(true);
  }

  onRemove(id: number) {
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to delete task analysis documents.'
      );
      return;
    }
    const singular = this.utils.getLangTerm(
      'task_analysis_documents.singular',
      'Task Analysis Document'
    );
    this.utils.showModal(
      `Remove ${singular}`,
      `Are you sure you want to remove this ${singular}?`,
      () => {
        this.api
          .makeRequest('delete', 'v1/task-analysis/' + id)
          .then((response: any) => {
            this.utils.showToast(`The ${singular} was removed.`);
            this.dataSource.selection.deselect(id);
            this.dataSource.getData();
          })
          .catch((err: any) => {
            this.utils.showModal('Error', err.error.message);
          });
      }
    );
  }

  onDeleteSelected() {
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to delete task analysis documents.'
      );
      return;
    }
    const singular = this.utils.getLangTerm(
      'task_analysis_documents.singular',
      'Task Analysis Document'
    );
    this.utils.showModal(
      `Remove ${singular}`,
      `Are you sure you want to remove the selected ${singular}?`,
      () => {
        this.api
          .makeRequest(
            'delete',
            'v1/task-analysis/' + this.dataSource.selection.selected.join(',')
          )
          .then((response: any) => {
            this.utils.showToast(`The selected ${singular} was removed`);
            this.dataSource.selection.clear();
            this.dataSource.getData();
          })
          .catch((err: any) => {
            this.utils.showModal('Error', err.error.message);
          });
      }
    );
  }

  onExportSelected(type: string = 'pdf') {
    // make a request to download a document
    this.api
      .makeDownloadRequest(
        'v1/hazardous_substances/export/' +
          type +
          '/' +
          this.dataSource.selection.selected.join(',')
      )
      .then((response: any) => {
        // handle internet explorer - No longer supported
        // if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        //   window.navigator.msSaveOrOpenBlob(response);
        //   return;
        // }

        // get current date object
        const date = new Date();

        // handle other browsers
        const data = window.URL.createObjectURL(response);
        // create a download link
        const link = document.createElement('a');
        link.href = data;
        link.download =
          'hazardous_substances-' +
          date.getFullYear() +
          (date.getMonth() + 1) +
          date.getDate() +
          '.' +
          type;
        link.click(); // initiate the download

        // For Firefox it is necessary to delay revoking the ObjectURL
        setTimeout(function() {
          window.URL.revokeObjectURL(data);
        }, 100);
      })
      .catch((err: any) => {
        this.utils.showModal('Error', err.error.message);
      });
  }

  onFilter() {
    this.utils.showComponentDialog(
      SitesTaskAnalysesFilterComponent,
      {
        site_ids: this.dataSource.site_ids,
        parent_site_id: this.parent_id
      },
      {
        width: '500px'
      },
      (results) => {
        if (typeof results !== 'undefined') {
          this.dataSource.site_ids = results.site_ids;

          if (this.dataSource.site_ids.length === 0) {
            this.dataSource.site_ids.push(this.site_id);
          }

          this.dataSource.getData(true);
        }
      }
    );
  }

  /**
   * Format the text display for associated sites.
   * Examples:
   *  - Head Office
   *  - 0 Sites
   *  - 1 Site (If name is not accessible otherwise the name of the site)
   *  - 2 Sites
   *  - Etc.
   * @param form
   */
  formatSitesListColumnText(task_analysis: TaskAnalysisModel) {
    // Check if we have one user.
    if (task_analysis.sites_count == 1) {
      // Check if we have details of one user.
      if (task_analysis.sites[0]) {
        return task_analysis.sites[0].name;
      }
      // Respond with a generic 1 User response.
      return (
        task_analysis.sites_count +
        ' ' +
        this.utils.getLangTerm('parent-child-sites-combined.singular', 'Site')
      );
    }
    // Respond with the users count.
    return (
      task_analysis.sites_count +
      ' ' +
      this.utils.getLangTerm('parent-child-sites-combined.plural', 'Sites')
    );
  }

  /**
   * The tooltip text is only used if there is more than 1 site.
   * @param form
   */
  formatSitesListColumnTooltip(task_analysis: TaskAnalysisModel) {
    if (task_analysis.sites_count > 1) {
      const site_names: string[] = task_analysis.sites.map(
        (site: SiteModel) => {
          return site.name;
        }
      );
      return site_names.join(`\n`);
    }
    return '';
  }
}

export class TaskAnalysisDataSource extends CustomDataSource {
  sort_by = 'task_name';
  sort_order = 'asc';

  site_ids: number[] = [];

  getData(resetOffset: boolean = false) {
    this.getDataFromLaravelAPI(`task-analysis`, resetOffset, () => {}, {
      site_ids: this.site_ids.join(',')
    });
  }
}
